import { useEffect } from "react";
import { useWebSocket } from "contexts/WebSocketContext";
import { useAuth } from "contexts/AuthContext";
import { useStateContext } from "contexts/ContextProvider";

/**
 * Hook to listen for assignment updates and trigger refetch + notification
 * @param {Object} params
 * @param {string} params.modelType - Type of model (e.g., "Interpretation", "Translation", "OtherService")
 * @param {number} params.recordId - ID of the assignment/record
 * @param {Function} params.onUpdate - Callback function to refetch data (called when update is received, receives updatedBy object)
 * @param {Function} params.toastFn - Toast notification function (optional, for custom toast handling)
 * @returns {void}
 */
export const useAssignmentUpdates = ({
  modelType,
  recordId,
  onUpdate,
  toastFn,
}) => {
  const { socket } = useWebSocket();
  const { currentUser } = useAuth();
  const { showNotification } = useStateContext();

  useEffect(() => {
    if (!socket || !modelType || !recordId || !onUpdate) {
      return;
    }

    // Handler for assignment updates
    const handleAssignmentUpdate = (payload = {}) => {
      // Normalize various event payload shapes (assignment:updated, service:updated, etc.)
      const updatedBy = payload.updatedBy || payload.updated_by || payload.updater || null;
      const updateModelType = payload.modelType || payload.model_type || payload.model || payload.event || modelType;
      const updateRecordId =
        payload.recordId ?? payload.record_id ?? payload.record ?? payload.serviceId ?? payload.service_id ?? null;
      const projectNumber = payload.projectNumber || payload.project_number || null;
      const changeType = payload.changeType || payload.change_type || payload.event || null;
      const changeAction = payload.changeAction || payload.change_action || null;

      if (!updateRecordId) return;

      const recordIdNumberMatch = Number(updateRecordId) === Number(recordId);

      // Only process if this is for our current assignment (modelType match is optional/lenient)
      if (recordIdNumberMatch) {
        // If updatedBy has a userId and it's the current user, skip the notification
        if (updatedBy && updatedBy.userId && updatedBy.userId === currentUser?.id) {
          return;
        }

        const meta = {
          projectNumber,
          changeType,
          changeAction,
          modelType: updateModelType,
          recordId: updateRecordId,
        };

        const notifierName = (updatedBy && (updatedBy.userName || updatedBy.name)) || payload.actorName || payload.actor || "Another member";

        // Show toast/notification
        if (toastFn) {
          toastFn(`${notifierName} updated the assignment. Check History for more details.`);
        } else if (showNotification) {
          showNotification({ type: "info", message: `${notifierName} updated the assignment. Check History for more details.` });
        }

        // Trigger refetch with updater info
        onUpdate(updatedBy || { userName: notifierName, userId: null }, meta);
      }
    };

    // Listen for assignment:updated events and legacy/service updates
    socket.on("assignment:updated", handleAssignmentUpdate);
    socket.on("service:updated", handleAssignmentUpdate);

    // Cleanup
    return () => {
      socket.off("assignment:updated", handleAssignmentUpdate);
      socket.off("service:updated", handleAssignmentUpdate);
    };
  }, [
    socket,
    modelType,
    recordId,
    onUpdate,
    currentUser?.id,
    showNotification,
    toastFn,
  ]);
};

export default useAssignmentUpdates;
